/**
 * Admin Analytics Dashboard
 * 
 * Weekly Close funnel health visualization.
 * Admin-only access required.
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { AppLayout } from "./DashboardPage";
import { Button } from "../components/ui/button";
import { 
  BarChart3, 
  TrendingUp, 
  Clock, 
  Users,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Loader2
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from "recharts";
import api from "../lib/api";

// Format number with commas
function formatNumber(num) {
  return num?.toLocaleString() || "0";
}

// Format seconds to human readable
function formatDuration(seconds) {
  if (!seconds) return "N/A";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${Math.round(seconds / 3600)}h`;
}

// Metric Card Component
function MetricCard({ title, value, subtitle, icon: Icon, color = "default" }) {
  const colorClasses = {
    default: "bg-card",
    success: "bg-emerald-50 border-emerald-200",
    warning: "bg-amber-50 border-amber-200",
    info: "bg-blue-50 border-blue-200"
  };

  return (
    <div className={`p-4 rounded-xl border ${colorClasses[color]}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-semibold mt-1">{value}</p>
          {subtitle && (
            <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
          )}
        </div>
        {Icon && (
          <div className="w-10 h-10 rounded-full bg-[#1a4d3e]/10 flex items-center justify-center">
            <Icon className="w-5 h-5 text-[#1a4d3e]" />
          </div>
        )}
      </div>
    </div>
  );
}

// Funnel Chart Component
function FunnelChart({ data }) {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No funnel data available
      </div>
    );
  }

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical">
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" />
          <YAxis dataKey="stage" type="category" width={80} />
          <Tooltip />
          <Bar 
            dataKey="count" 
            fill="#1a4d3e" 
            radius={[0, 4, 4, 0]}
            name="Sessions"
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// Interventions Breakdown Component
function InterventionsBreakdown({ data }) {
  if (!data || Object.keys(data).length === 0) {
    return <p className="text-sm text-muted-foreground">No interventions data</p>;
  }

  const total = Object.values(data).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-2">
      {Object.entries(data).map(([key, count]) => (
        <div key={key} className="flex items-center justify-between text-sm">
          <span className="capitalize">{key.replace(/_/g, " ")}</span>
          <div className="flex items-center gap-2">
            <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-[#1a4d3e] rounded-full"
                style={{ width: `${total > 0 ? (count / total * 100) : 0}%` }}
              />
            </div>
            <span className="text-muted-foreground w-8 text-right">{count}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// Main Admin Analytics Page
export default function AdminAnalyticsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [window, setWindow] = useState("7d");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState(null);
  const [funnel, setFunnel] = useState([]);

  // Check admin access
  useEffect(() => {
    if (user && !user.is_admin) {
      // For demo, allow access if email matches admin
      const adminEmail = "erana@arawealth.app";
      if (user.email !== adminEmail && user.email !== "demo@arawealth.com") {
        navigate("/dashboard");
      }
    }
  }, [user, navigate]);

  // Fetch analytics data
  useEffect(() => {
    fetchAnalytics();
  }, [window]);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch overview and funnel in parallel
      const [overviewResp, funnelResp] = await Promise.all([
        api.get(`/api/admin/analytics/weekly-close/overview?window=${window}`),
        api.get(`/api/admin/analytics/weekly-close/funnel?window=${window}`)
      ]);

      if (overviewResp.ok) {
        const data = await overviewResp.json();
        setOverview(data);
      } else {
        // Check if 403 (not admin)
        if (overviewResp.status === 403) {
          setError("Admin access required");
        } else {
          setError("Failed to load analytics");
        }
      }

      if (funnelResp.ok) {
        const data = await funnelResp.json();
        setFunnel(data.funnel || []);
      }
    } catch (err) {
      console.error("Analytics fetch error:", err);
      setError("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  const metrics = overview?.metrics || {};

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-semibold">Analytics Dashboard</h1>
            <p className="text-muted-foreground">Weekly Close funnel health</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Window Toggle */}
            <div className="flex bg-muted rounded-lg p-1">
              <button
                onClick={() => setWindow("7d")}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  window === "7d" ? "bg-white shadow" : "hover:bg-white/50"
                }`}
              >
                7 Days
              </button>
              <button
                onClick={() => setWindow("30d")}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  window === "30d" ? "bg-white shadow" : "hover:bg-white/50"
                }`}
              >
                30 Days
              </button>
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={fetchAnalytics}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && !overview && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Main Content */}
        {overview && (
          <>
            {/* Top Metrics Row */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <MetricCard
                title="Sessions Started"
                value={formatNumber(metrics.started_count)}
                subtitle={`${window === "7d" ? "Last 7 days" : "Last 30 days"}`}
                icon={Users}
              />
              <MetricCard
                title="Sessions Finalized"
                value={formatNumber(metrics.finalized_count)}
                subtitle="Completed weekly closes"
                icon={CheckCircle2}
                color="success"
              />
              <MetricCard
                title="WCCR"
                value={`${metrics.wccr || 0}%`}
                subtitle="Weekly Close Completion Rate"
                icon={TrendingUp}
                color={metrics.wccr >= 70 ? "success" : metrics.wccr >= 50 ? "warning" : "default"}
              />
              <MetricCard
                title="Median Time to Finalize"
                value={formatDuration(metrics.median_time_to_finalize_seconds)}
                subtitle="From start to complete"
                icon={Clock}
                color="info"
              />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Funnel Chart */}
              <div className="bg-card border rounded-xl p-6">
                <h3 className="font-heading text-lg font-semibold mb-4">
                  Funnel Drop-off
                </h3>
                <FunnelChart data={funnel} />
                
                {/* Drop-off details */}
                {metrics.dropoffs_by_stage && (
                  <div className="mt-4 pt-4 border-t space-y-2">
                    <p className="text-sm font-medium text-muted-foreground">Drop-offs by Stage</p>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Start → Compute</p>
                        <p className="font-medium">{metrics.dropoffs_by_stage.start_to_compute || 0}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Compute → Review</p>
                        <p className="font-medium">{metrics.dropoffs_by_stage.compute_to_review || 0}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Review → Finalize</p>
                        <p className="font-medium">{metrics.dropoffs_by_stage.review_to_finalize || 0}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Interventions Card */}
              <div className="bg-card border rounded-xl p-6">
                <h3 className="font-heading text-lg font-semibold mb-4">
                  Calm Interventions
                </h3>
                
                <div className="space-y-6">
                  {/* Shown vs Selected */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-muted/30 rounded-lg">
                      <p className="text-sm text-muted-foreground">Shown</p>
                      <p className="text-2xl font-semibold">
                        {formatNumber(metrics.interventions_shown_count)}
                      </p>
                    </div>
                    <div className="p-4 bg-[#1a4d3e]/5 rounded-lg">
                      <p className="text-sm text-muted-foreground">Selected</p>
                      <p className="text-2xl font-semibold">
                        {formatNumber(
                          Object.values(metrics.interventions_selected_breakdown || {})
                            .reduce((a, b) => a + b, 0)
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Breakdown */}
                  <div>
                    <p className="text-sm font-medium mb-3">Selection Breakdown</p>
                    <InterventionsBreakdown data={metrics.interventions_selected_breakdown} />
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Metrics Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <MetricCard
                title="Week Override Rate"
                value={`${metrics.week_override_rate || 0}%`}
                subtitle={`${metrics.week_override_count || 0} overrides`}
                icon={AlertCircle}
              />
              <MetricCard
                title="Abandoned Sessions"
                value={formatNumber(metrics.abandoned_count)}
                subtitle="Started but not finalized within 24h"
                icon={AlertCircle}
              />
              <MetricCard
                title="Data Source"
                value={overview.source === "rollups" ? "Rollups" : "Events"}
                subtitle={`Environment: ${overview.environment}`}
                icon={BarChart3}
              />
            </div>

            {/* Metadata */}
            <div className="text-xs text-muted-foreground text-center pt-4">
              Data range: {overview.date_range?.start?.split("T")[0]} to {overview.date_range?.end?.split("T")[0]}
              {" · "}
              Privacy-safe aggregates only (no PII)
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}
